import { NavLink } from 'react-router-dom';
import { NAV_ITEMS } from '@/lib/navigation';
import { useAuth } from '@/contexts/AuthContext';

const LOGO_URL =
  'https://static.readdy.ai/image/5e0dc67cda370742b3d5bcd513fed04e/7ccb60a178e69ccb6f070627bdcb7304.jpeg';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  onLogoutRequest: () => void;
}

export default function Sidebar({ open, onClose, onLogoutRequest }: SidebarProps) {
  const { profile, isSuperUsuario } = useAuth();

  const items = NAV_ITEMS.filter((item) => !item.superOnly || isSuperUsuario);

  return (
    <>
      {open && (
        <button
          type="button"
          aria-label="Cerrar menú"
          onClick={onClose}
          className="fixed inset-0 z-40 cursor-pointer bg-foreground-950/40 animate-fade-in lg:hidden"
        />
      )}

      <aside
        className={[
          'fixed inset-y-0 left-0 z-50 flex h-screen w-64 flex-col border-r border-background-200 bg-background-50',
          'transition-transform duration-300 lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        <div className="flex h-16 shrink-0 items-center gap-3 border-b border-background-200 px-4">
          <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md border border-background-200 bg-background-50">
            <img
              src={LOGO_URL}
              alt="IZZY Corporation"
              className="h-full w-full object-cover"
            />
          </div>
          <div className="min-w-0">
            <p className="font-heading text-sm font-bold leading-tight text-foreground-950">
              IZZY Corporation
            </p>
            <p className="truncate text-[11px] text-foreground-500">Sistema de Seguimiento</p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <p className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-foreground-400">
            Menú
          </p>
          <ul className="flex flex-col gap-1">
            {items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  onClick={onClose}
                  className={({ isActive }) =>
                    [
                      'group relative flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200',
                      isActive
                        ? 'bg-primary-100 text-primary-800'
                        : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900',
                    ].join(' ')
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-primary-500"></span>
                      )}
                      <span className="flex h-5 w-5 items-center justify-center">
                        <i className={`${item.icon} text-lg`}></i>
                      </span>
                      <span className="truncate">{item.label}</span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="shrink-0 border-t border-background-200 p-3">
          <div className="mb-2 flex items-center gap-3 rounded-md bg-background-100 px-3 py-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
              <i className="ri-user-line text-lg"></i>
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground-900">
                {profile?.full_name || profile?.username || 'Usuario'}
              </p>
              <p className="truncate text-[11px] text-foreground-500">
                {isSuperUsuario ? 'Super usuario' : 'Usuario'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onLogoutRequest}
            className="flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-red-600 transition-colors duration-200 hover:bg-red-50"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-logout-box-r-line text-lg"></i>
            </span>
            Cerrar sesión
          </button>
        </div>
      </aside>
    </>
  );
}